import debounce from 'debounce-promise';
import { FinnishSSN } from 'finnish-ssn';
import Highlighter from 'react-highlight-words';
import { useTranslation } from 'react-i18next';
import { OptionsType } from 'react-select';
import AsyncSelect from 'react-select/async';

import { useRootStore } from '../../providers/RootStoreProvider';
import { SavedCustomer } from '../../types';
import { selectStyles } from '../../utils';

const Q_DEBOUNCE = 100;

export interface CustomerInputProps {
    onChange: (customer: SavedCustomer) => any;
    value: SavedCustomer | null;
}

const CustomerInput = (props: CustomerInputProps) => {
    const { dataStore } = useRootStore();
    const { t } = useTranslation();

    const loadOptions = async (input: string): Promise<readonly SavedCustomer[]> => {
        const query = input.trim();

        if (FinnishSSN.validate(input)) {
            const customer = await dataStore.getCustomerBySsn(input);
            return customer ? [customer] : [];
        }

        return await dataStore.getCustomers(query, true);
    };
    const debouncedLoadOptions = debounce(loadOptions, Q_DEBOUNCE);

    const highlight = (customer: SavedCustomer | undefined) => {
        const searchStrings = [
            customer?.firstName,
            customer?.lastName,
            customer?.phoneNumber ? customer.phoneNumber.replace(/[^0-9+]/g, '') : undefined,
            customer?.email,
        ];

        return searchStrings.filter(Boolean).join(', ');
    };

    const formatOptionLabel = (option: any, { inputValue }: { inputValue: string }) => {
        if (option && option.__isNew__) {
            return option.label;
        }

        return (
            <Highlighter
                autoEscape={true} // important! otherwise some characters like `+` and `*` will cause an exception
                searchWords={inputValue.split(/[% ]+/).filter(Boolean)}
                textToHighlight={highlight(option)}
            />
        );
    };

    const isOptionSelected = (
        option: SavedCustomer,
        options: OptionsType<SavedCustomer>
    ): boolean => {
        return options.filter((i) => Boolean(i.ssn)).some((i) => i.ssn === option.ssn);
    };

    return (
        <AsyncSelect
            classNamePrefix="react-select"
            isClearable
            blurInputOnSelect={false}
            placeholder={t('select.customer.prompt')}
            loadOptions={debouncedLoadOptions as any}
            loadingMessage={() => t('general.loading')}
            formatOptionLabel={formatOptionLabel}
            noOptionsMessage={() => t('general.noOptions')}
            isOptionSelected={isOptionSelected}
            styles={selectStyles as any}
            menuPortalTarget={document.body}
            menuPosition={'fixed'}
            maxMenuHeight={200}
            onChange={(selectedCustomer) => {
                if (selectedCustomer) {
                    props.onChange(selectedCustomer);
                }
            }}
            value={props.value}
        />
    );
};

export default CustomerInput;
